/**
 * DOC-003 Continuous Scroll View + Dynamic TOC + Inline Commenting
 * Fetches first 10 pages WITH content from PDF API (skips empty pages).
 * Header shown ONCE at the top. Full TipTap editor in edit mode.
 * Comment system: select text -> floating button -> comment popup -> highlight + sidebar.
 * Used for BOTH read & edit modes of DOC-003 only.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Undo2, Redo2, Loader2,
  CheckCircle2, ChevronRight, MessageSquarePlus,
  Send, X, Reply, Check, Trash2, MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SimpleProgressBar } from '@/components/be-study/common/ProgressBar';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ═══════ CommentMark — custom TipTap Mark ═══════ */
const CommentMark = Mark.create({
  name: 'commentMark',
  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: el => el.getAttribute('data-comment-id'),
        renderHTML: attrs => {
          if (!attrs.commentId) return {};
          return { 'data-comment-id': attrs.commentId };
        },
      },
    };
  },
  parseHTML() { return [{ tag: 'span[data-comment-id]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'comment-highlight' }), 0];
  },
  addProseMirrorPlugins() {
    const markType = this.type;
    return [
      new Plugin({
        key: new PluginKey('commentMarkClick'),
        props: {
          handleClick(view, pos) {
            const resolved = view.state.doc.resolve(pos);
            const marks = resolved.marks();
            const commentMark = marks.find(m => m.type === markType);
            if (commentMark) {
              window.dispatchEvent(new CustomEvent('comment-mark-click', {
                detail: { commentId: commentMark.attrs.commentId },
              }));
            }
            return false;
          },
        },
      }),
    ];
  },
});

/* ═══════ Section title mapping ═══════ */
const KNOWN_TITLES = {
  1: 'Cover Page', 2: 'Title Page', 3: 'Principal Investigator & Facilities',
  4: 'Investigators & Participants', 5: 'Study Centres', 6: 'Study Period & Objectives',
  7: 'Methodology', 8: 'Test Product Details', 9: 'Reference Product Details',
  10: 'Criteria for Evaluation', 11: 'Summary & Conclusions',
  12: 'Cetirizine Analysis & BE Results', 13: 'Phenylephrine Analysis & BE Results',
  14: 'Caffeine Analysis & BE Results', 15: 'Paracetamol Analysis & BE Results',
};

function deriveSectionTitle(page) {
  if (KNOWN_TITLES[page.pageNumber]) return KNOWN_TITLES[page.pageNumber];
  const content = page.content || '';
  const headingMatch = content.match(/<h[23][^>]*>(.*?)<\/h[23]>/i);
  if (headingMatch) {
    const text = headingMatch[1].replace(/<[^>]+>/g, '').trim();
    if (text.length > 3 && text.length < 80) return text;
  }
  const boldMatch = content.match(/<strong>(.*?)<\/strong>/i);
  if (boldMatch) {
    const text = boldMatch[1].replace(/<[^>]+>/g, '').trim();
    if (text.length > 3 && text.length < 60) return text;
  }
  return `Section (Page ${page.pageNumber})`;
}

/* ═══════ Toolbar (edit mode) ═══════ */
function Toolbar({ editor }) {
  if (!editor) return null;
  const btn = (action, active, Icon, label) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); action(); }}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      title={label} data-testid={`doc003-tb-${label.toLowerCase().replace(/\s/g, '-')}`}
    ><Icon className="h-4 w-4" /></button>
  );
  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b bg-muted/30 flex-wrap sticky top-0 z-10" data-testid="doc003-toolbar">
      {btn(() => editor.chain().focus().toggleBold().run(), editor.isActive('bold'), Bold, 'Bold')}
      {btn(() => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'), Italic, 'Italic')}
      {btn(() => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline'), UnderlineIcon, 'Underline')}
      {btn(() => editor.chain().focus().toggleStrike().run(), editor.isActive('strike'), Strikethrough, 'Strike')}
      <span className="w-px h-5 bg-border mx-1" />
      {btn(() => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive('heading', { level: 1 }), Heading1, 'H1')}
      {btn(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }), Heading2, 'H2')}
      {btn(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }), Heading3, 'H3')}
      <span className="w-px h-5 bg-border mx-1" />
      {btn(() => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'), List, 'Bullet List')}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'), ListOrdered, 'Numbered List')}
      <span className="w-px h-5 bg-border mx-1" />
      {btn(() => editor.chain().focus().undo().run(), false, Undo2, 'Undo')}
      {btn(() => editor.chain().focus().redo().run(), false, Redo2, 'Redo')}
    </div>
  );
}

/* ═══════ Combine pages ═══════ */
function combinePages(pages) {
  let headerHtml = '';
  const bodyParts = [];
  pages.forEach((p, i) => {
    if (!headerHtml && p.header) headerHtml = p.header;
    if (p.content && p.content.trim()) bodyParts.push(p.content);
    if (i < pages.length - 1) bodyParts.push('<hr class="doc003-page-break"/>');
  });
  return { headerHtml, bodyHtml: bodyParts.join('\n') };
}

/* ═══════ Timestamp formatter ═══════ */
function formatTimestamp(ts) {
  const d = new Date(ts);
  const diffMin = Math.floor((Date.now() - d) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* ═══════ Avatar ═══════ */
function UserAvatar({ user, size = 'md' }) {
  const sz = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-7 h-7 text-[11px]';
  return (
    <div className={cn(sz, 'rounded-full flex items-center justify-center font-bold text-white shrink-0', user.color)}
      title={user.name}>
      {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
    </div>
  );
}

/* ═══════ Main Component ═══════ */
export function Doc003ContinuousView({ isEditMode, onUpdate, onTocReady, onCommentsChange, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [headerHtml, setHeaderHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [comments, setComments] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [selectionBubble, setSelectionBubble] = useState(null);
  const [commentPopup, setCommentPopup] = useState(null);
  const [commentText, setCommentText] = useState('');
  const editorWrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const commentUser = useMemo(() => currentUser ? {
    id: currentUser.id, name: currentUser.name, role: currentUser.role,
    color: currentUser.role === 'ADMIN' ? 'bg-violet-600' : currentUser.role === 'REVIEWER' ? 'bg-emerald-600' : 'bg-blue-600',
  } : { id: 'u1', name: 'User', role: 'User', color: 'bg-blue-600' }, [currentUser]);

  // Fetch pages
  useEffect(() => {
    fetch(`${API_BASE}/api/pdf/bulk-content?count=10`)
      .then(r => r.json())
      .then(data => {
        const pages = data.pages || [];
        const { headerHtml: hdr, bodyHtml: body } = combinePages(pages);
        setHeaderHtml(hdr);
        setBodyHtml(body);
        setLoading(false);
        if (onTocReady) {
          onTocReady(pages.map((p, i) => ({
            id: `doc003-sec-${i}`, title: deriveSectionTitle(p), pageNumber: p.pageNumber,
          })));
        }
      })
      .catch(() => setLoading(false));
  }, [onTocReady]);

  // Notify parent of comment changes
  useEffect(() => {
    if (onCommentsChange) onCommentsChange(comments, activeCommentId, setActiveCommentId);
  }, [comments, activeCommentId, onCommentsChange]);

  // TipTap editor with CommentMark
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      CommentMark,
    ],
    content: '<p>Loading...</p>',
    editable: isEditMode,
    onSelectionUpdate: ({ editor: ed }) => {
      if (commentPopup) return;
      const { from, to } = ed.state.selection;
      if (from === to) { setSelectionBubble(null); return; }
      const selectedText = ed.state.doc.textBetween(from, to, ' ');
      if (!selectedText.trim()) { setSelectionBubble(null); return; }
      const scrollEl = scrollContainerRef.current;
      if (!scrollEl) return;
      const coords = ed.view.coordsAtPos(from);
      const endCoords = ed.view.coordsAtPos(to);
      const scrollRect = scrollEl.getBoundingClientRect();
      const topInScroll = ((coords.top + endCoords.bottom) / 2) - scrollRect.top + scrollEl.scrollTop - 16;
      setSelectionBubble({ from, to, selectedText, top: topInScroll });
    },
  });

  // Set content
  useEffect(() => {
    if (editor && bodyHtml) editor.commands.setContent(bodyHtml);
  }, [editor, bodyHtml]);

  // Forward editor updates
  useEffect(() => {
    if (!editor || !isEditMode || !onUpdate) return;
    const handler = () => onUpdate(editor.getHTML());
    editor.on('update', handler);
    return () => editor.off('update', handler);
  }, [editor, isEditMode, onUpdate]);

  // Listen for comment mark clicks
  useEffect(() => {
    const handler = (e) => setActiveCommentId(e.detail.commentId);
    window.addEventListener('comment-mark-click', handler);
    return () => window.removeEventListener('comment-mark-click', handler);
  }, []);

  // Scroll to highlighted text on active comment change
  useEffect(() => {
    if (!activeCommentId || !editorWrapperRef.current) return;
    const el = editorWrapperRef.current.querySelector(`[data-comment-id="${activeCommentId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('comment-flash');
      setTimeout(() => el.classList.remove('comment-flash'), 1200);
    }
  }, [activeCommentId]);

  const openCommentPopup = useCallback(() => {
    if (!selectionBubble) return;
    setCommentPopup({ ...selectionBubble });
    setSelectionBubble(null);
    setCommentText('');
  }, [selectionBubble]);

  const cancelPopup = useCallback(() => {
    setCommentPopup(null);
    setCommentText('');
  }, []);

  const submitComment = useCallback(() => {
    if (!commentPopup || !commentText.trim() || !editor) return;
    const commentId = `cmt-${Date.now()}`;
    editor.chain().focus()
      .setTextSelection({ from: commentPopup.from, to: commentPopup.to })
      .setMark('commentMark', { commentId })
      .run();
    setComments(prev => [...prev, {
      id: commentId, selectedText: commentPopup.selectedText,
      text: commentText.trim(), author: commentUser,
      createdAt: new Date().toISOString(), resolved: false, replies: [],
    }]);
    setActiveCommentId(commentId);
    setCommentPopup(null);
    setCommentText('');
  }, [commentPopup, commentText, editor, commentUser]);

  const handleReply = useCallback((commentId, replyText) => {
    setComments(prev => prev.map(c =>
      c.id === commentId ? {
        ...c, replies: [...c.replies, {
          id: `rpl-${Date.now()}`, text: replyText,
          author: commentUser, createdAt: new Date().toISOString(),
        }],
      } : c
    ));
  }, [commentUser]);

  const handleResolve = useCallback((commentId) => {
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, resolved: true } : c));
  }, []);

  const handleDelete = useCallback((commentId) => {
    if (editor) {
      editor.state.doc.descendants((node, pos) => {
        node.marks.forEach(mark => {
          if (mark.type.name === 'commentMark' && mark.attrs.commentId === commentId) {
            editor.chain()
              .setTextSelection({ from: pos, to: pos + node.nodeSize })
              .unsetMark('commentMark')
              .run();
          }
        });
      });
    }
    setComments(prev => prev.filter(c => c.id !== commentId));
    if (activeCommentId === commentId) setActiveCommentId(null);
  }, [editor, activeCommentId]);

  // Expose comment actions for parent to use
  useEffect(() => {
    if (onCommentsChange) {
      onCommentsChange(comments, activeCommentId, setActiveCommentId, { handleReply, handleResolve, handleDelete });
    }
  }, [comments, activeCommentId, onCommentsChange, handleReply, handleResolve, handleDelete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3" data-testid="doc003-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Loading BE Study Report...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" ref={editorWrapperRef}
      data-testid={isEditMode ? 'doc003-edit-continuous' : 'doc003-read-continuous'}>
      {isEditMode && <Toolbar editor={editor} />}

      <div className="flex-1 overflow-auto bg-[hsl(var(--muted)/0.15)] relative" ref={scrollContainerRef}>
        <div className="doc003-document-page">
          {headerHtml && (
            <div className="doc003-header-html" data-testid="doc003-header"
              dangerouslySetInnerHTML={{ __html: headerHtml }} />
          )}
          <div className="tiptap-doc003-content">
            {editor && <EditorContent editor={editor} />}
          </div>
        </div>

        {/* Floating "Add Comment" button on text selection */}
        {isEditMode && selectionBubble && !commentPopup && (
          <button
            className="absolute z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium shadow-lg transition-all animate-in fade-in zoom-in-95 duration-150"
            style={{ top: selectionBubble.top, right: 16 }}
            onMouseDown={e => e.preventDefault()}
            onClick={openCommentPopup}
            data-testid="doc003-floating-comment-btn"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" /> Comment
          </button>
        )}

        {/* Comment input popup */}
        {isEditMode && commentPopup && (
          <div className="absolute z-30 w-80 bg-card border rounded-lg shadow-2xl p-3 space-y-2.5 animate-in fade-in slide-in-from-right-2 duration-150"
            style={{ top: commentPopup.top, right: 16 }}
            onMouseDown={e => e.stopPropagation()}
            data-testid="doc003-comment-popup">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <UserAvatar user={commentUser} size="sm" />
                <span className="text-xs font-medium">{commentUser.name}</span>
                <Badge variant="secondary" className="text-[9px] h-4 px-1">{commentUser.role}</Badge>
              </div>
              <button onClick={cancelPopup} className="text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-[11px] text-muted-foreground italic bg-muted/50 rounded px-2 py-1 border-l-2 border-amber-400 line-clamp-2">
              "{commentPopup.selectedText}"
            </div>
            <div className="flex gap-1.5">
              <Input autoFocus value={commentText} onChange={e => setCommentText(e.target.value)}
                placeholder="Add a comment..." className="h-8 text-xs"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                data-testid="doc003-comment-input" />
              <Button size="sm" className="h-8 px-2.5" onClick={submitComment} disabled={!commentText.trim()}
                data-testid="doc003-comment-submit">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════ Comment Sidebar for DOC-003 ═══════ */
export function Doc003CommentSidebar({ comments, activeCommentId, onSetActive, onReply, onResolve, onDelete, currentUser, onCollapse }) {
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState('');
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeCommentId && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeCommentId]);

  const handleReply = (commentId) => {
    if (!replyText.trim()) return;
    onReply(commentId, replyText.trim());
    setReplyText('');
    setReplyTarget(null);
  };

  const sorted = useMemo(() =>
    [...comments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [comments]
  );

  return (
    <div className="flex flex-col h-full" data-testid="doc003-comment-sidebar">
      <div className="px-3 py-2.5 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Comments</span>
          {comments.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{comments.length}</Badge>
          )}
        </div>
        {onCollapse && (
          <button onClick={onCollapse}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Collapse panel" data-testid="collapse-right-btn">
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageSquarePlus className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-xs">Select text in Edit mode</p>
              <p className="text-xs font-medium">to add comments</p>
            </div>
          ) : (
            sorted.map(comment => {
              const isActive = comment.id === activeCommentId;
              return (
                <div key={comment.id} ref={isActive ? activeRef : null}
                  onClick={() => onSetActive(comment.id)}
                  className={cn(
                    'rounded-lg border p-2.5 cursor-pointer transition-all text-sm',
                    isActive ? 'border-primary/50 bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'hover:border-muted-foreground/20 hover:bg-muted/30',
                    comment.resolved && 'opacity-60'
                  )}
                  data-testid={`doc003-comment-card-${comment.id}`}>
                  <div className="text-[11px] text-muted-foreground italic bg-muted/50 rounded px-2 py-1 mb-2 line-clamp-2 border-l-2 border-amber-400">
                    "{comment.selectedText}"
                  </div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <UserAvatar user={comment.author} size="sm" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold block truncate">{comment.author.name}</span>
                      <span className="text-[10px] text-muted-foreground">{comment.author.role} · {formatTimestamp(comment.createdAt)}</span>
                    </div>
                    {!comment.resolved && (
                      <button onClick={(e) => { e.stopPropagation(); onResolve(comment.id); }}
                        className="text-[10px] text-green-600 hover:underline flex items-center gap-0.5 shrink-0">
                        <Check className="h-3 w-3" /> Resolve
                      </button>
                    )}
                    {comment.resolved && (
                      <Badge variant="outline" className="text-[10px] text-green-600 border-green-200 shrink-0">
                        <Check className="h-3 w-3 mr-0.5" /> Resolved
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed mb-2">{comment.text}</p>
                  {comment.replies.length > 0 && (
                    <div className="pl-3 border-l-2 border-muted space-y-2 mb-2">
                      {comment.replies.map(reply => (
                        <div key={reply.id} className="text-xs" data-testid={`doc003-reply-${reply.id}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <UserAvatar user={reply.author} size="sm" />
                            <span className="font-medium">{reply.author.name}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto">{formatTimestamp(reply.createdAt)}</span>
                          </div>
                          <p className="pl-6 leading-relaxed">{reply.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {replyTarget === comment.id ? (
                    <div className="flex gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
                      <Input autoFocus value={replyText} onChange={e => setReplyText(e.target.value)}
                        placeholder="Write a reply..." className="h-7 text-xs"
                        onKeyDown={e => e.key === 'Enter' && handleReply(comment.id)}
                        data-testid={`doc003-reply-input-${comment.id}`} />
                      <Button size="sm" className="h-7 px-2" onClick={() => handleReply(comment.id)} disabled={!replyText.trim()}>
                        <Send className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => { setReplyTarget(null); setReplyText(''); }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={(e) => { e.stopPropagation(); setReplyTarget(comment.id); }}
                        className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
                        <Reply className="h-3 w-3" /> Reply
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onDelete(comment.id); }}
                        className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-0.5 ml-auto">
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ═══════ Dynamic TOC ═══════ */
export function Doc003DynamicToc({ headings, activeId, onSelect, onCollapse }) {
  const handleClick = useCallback((section, idx) => {
    if (onSelect) onSelect(section.id);
    const editorEl = document.querySelector('.tiptap-doc003-content .ProseMirror');
    if (!editorEl) return;
    const scrollContainer = editorEl.closest('[class*="overflow-auto"]');
    if (!scrollContainer) return;
    if (idx === 0) { scrollContainer.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const hrs = editorEl.querySelectorAll('hr');
    const targetHr = hrs[idx - 1];
    if (targetHr) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const hrRect = targetHr.getBoundingClientRect();
      const offset = hrRect.top - containerRect.top + scrollContainer.scrollTop;
      scrollContainer.scrollTo({ top: offset - 10, behavior: 'smooth' });
    }
  }, [onSelect]);

  const sections = headings && headings.length > 0 ? headings : [];
  const progress = sections.length > 0 ? 100 : 0;

  return (
    <div className="flex flex-col h-full" data-testid="doc003-dynamic-toc">
      <div className="p-3 border-b flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">Table of Contents</p>
        {onCollapse && (
          <button onClick={onCollapse}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Collapse panel" data-testid="collapse-left-btn">
            <List className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="px-3 pt-2 pb-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span>Progress</span></div>
        <SimpleProgressBar value={progress} size="sm" />
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {sections.map((s, idx) => (
            <button key={s.id} onClick={() => handleClick(s, idx)}
              className={cn(
                'w-full flex items-start gap-2 px-2.5 py-2.5 rounded-md text-left text-sm transition-colors',
                activeId === s.id ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary' : 'hover:bg-muted text-foreground'
              )}
              data-testid={`toc-section-${s.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0"><span className="truncate block text-xs">{idx + 1}. {s.title}</span></div>
              <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0 mt-1" />
            </button>
          ))}
          {sections.length === 0 && <p className="text-xs text-muted-foreground px-2 py-4">Loading sections...</p>}
        </div>
      </ScrollArea>
    </div>
  );
}
